import { ApiEstimationItemPurpose } from '@/api/schema/data-contracts';
import type { EstimationWithDependencies } from '@/hooks/useGetEstimationWithDependencies';

import type { CostEstimationItemDetail, EstimationItemDetail } from '../model';
import {
  buildEstimationItemsRecordIndex,
  getCostEstimationItemsTableRecord,
  getResourceEstimationItemsTableRecord,
} from './estimationItemsTableRecordMapper';

export const getResourceEstimationItemDetail = (
  estimationItemId: string,
  data: EstimationWithDependencies,
): EstimationItemDetail => {
  const estimationItem = data.estimationItems.find((x) => x.id === estimationItemId)!;
  if (estimationItem.purpose !== ApiEstimationItemPurpose.RESOURCE) {
    throw new Error('Wrong estimation item purpose');
  }
  const estimationItemTableRecord = getResourceEstimationItemsTableRecord(
    estimationItem,
    buildEstimationItemsRecordIndex(data),
  );
  const estimationModelItem = data.estimationModel.items.find(
    (x) => x.id === estimationItem.estimationModelItemId,
  );
  return {
    id: estimationItem.id,
    reference: estimationItemTableRecord.reference,
    parentId: estimationItem.parentId ?? undefined,
    name: estimationItem.name,
    description: estimationItem.description ?? '',
    phaseLeadId: estimationItemTableRecord.phaseLeadId,
    methodologyId: estimationItem.methodologyId ?? undefined,
    // An item can carry a group id the current model version no longer has.
    groupName:
      data.estimationModel.itemGroups.find(
        (group) => group.id === estimationItem.estimationModelGroupId,
      )?.name ?? '',
    isParentBasedCalculation: estimationItem.isParentBasedCalculation,
    calculatedResult: estimationItem.calculatedResult ?? undefined,
    hasEstimationModelItemParameters: !!estimationModelItem?.parameters.length || false,
    unit: estimationItem.unit,
    subItemsCalculatedResultsSum: data.estimationItems
      .filter((x) => x.parentId === estimationItem.id)
      .map((x) => (x.calculatedResult === null ? undefined : x.calculatedResult))
      // sum or undefined
      .reduce<number | undefined>((a, b) => {
        if (b === undefined) {
          return a;
        }
        if (a === undefined) {
          return b;
        }
        return a + b;
      }, undefined),
    assumptionsCount: estimationItemTableRecord.assumptionsCount,
    params: estimationItemTableRecord.params,
    resources: estimationItemTableRecord.resources,
  };
};

export const getCostEstimationItemDetail = (
  estimationItemId: string,
  data: EstimationWithDependencies,
): CostEstimationItemDetail => {
  const estimationItem = data.estimationItems.find((x) => x.id === estimationItemId)!;
  if (estimationItem.purpose !== ApiEstimationItemPurpose.COST) {
    throw new Error('Wrong estimation item purpose');
  }
  if (!data.estimationItemCosts) {
    throw new Error('Estimation items costs cannot be undefined');
  }
  const estimationItemTableRecord = getCostEstimationItemsTableRecord(
    estimationItem,
    buildEstimationItemsRecordIndex(data),
  );
  const estimationModelItem = data.estimationModel.items.find(
    (x) => x.id === estimationItem.estimationModelItemId,
  );
  return {
    id: estimationItem.id,
    reference: estimationItemTableRecord.reference,
    parentId: estimationItem.parentId ?? undefined,
    name: estimationItem.name,
    description: estimationItem.description ?? '',
    // An item can carry a group id the current model version no longer has.
    groupName:
      data.estimationModel.itemGroups.find(
        (group) => group.id === estimationItem.estimationModelGroupId,
      )?.name ?? '',
    isParentBasedCalculation: estimationItem.isParentBasedCalculation,
    hasEstimationModelItemParameters: !!estimationModelItem?.parameters.length || false,
    subItemsCalculatedResultsSum: data.estimationItems
      .filter((x) => x.parentId === estimationItem.id)
      .map((x) => (x.calculatedResult === null ? undefined : x.calculatedResult))
      // sum or undefined
      .reduce<number | undefined>((a, b) => {
        if (b === undefined) {
          return a;
        }
        if (a === undefined) {
          return b;
        }
        return a + b;
      }, undefined),
    assumptionsCount: estimationItemTableRecord.assumptionsCount,
    estimationItemsCosts: estimationItemTableRecord.estimationItemCosts,
  };
};
